import React, { useEffect, useState } from "react";
import "./SelectTemplate.scss";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { CommonEnums } from "../../../../enums/common.enums";
import { TemplatesApi } from "../../../../utils/api/Templates";
import Loading from "../../../loading/Loading";
import { getImageUrl } from "../../../../utils/helpers/common";
import VidioIcon from "../../../../components/icons/VidioIcon";
import RoundTrueIcon from "../../../../components/icons/RoundTrueIcon";
import TemplateButtons from "./TemplateButtons";
import { AuthHelpers } from "../../../../utils/auth.helpers";

interface StateType {
  isLoading: boolean;
  listData: any[];
  totalPage: number;
  pageNumber: number;
}

interface Props {
  subscriptionTypeProp?: string;
  listingTypeProp?: string;
  postTypeProp?: string;
  templateTypeIdProp?: string;
  hideSelectedTemplate?: boolean;
  onSelectTemplate?: (id: string, imageUrl: string) => void;
  isAnnouncementPost?: boolean;
  announcementPostId?: string;
}

const SelectTemplatesList: React.FC<Props> = ({
  subscriptionTypeProp,
  listingTypeProp,
  postTypeProp,
  templateTypeIdProp,
  hideSelectedTemplate,
  onSelectTemplate,
  isAnnouncementPost = false,
  announcementPostId,
}) => {
  const [state, setState] = useState<StateType>({
    isLoading: false,
    listData: [],
    totalPage: 1,
    pageNumber: 1,
  });

  const [searchParams] = useSearchParams();

  const subscriptionType =
    subscriptionTypeProp ||
    searchParams.get("subscription_type") ||
    "agent_marketing";
  const listingType = listingTypeProp || "sale";
  const postType = postTypeProp || searchParams.get("post_type") || "";
  const announcementType = searchParams.get("announcement_type") || "";
  const templateTypeId =
    templateTypeIdProp || searchParams.get("template_type") || "";

  const urlTemplateId = searchParams.get("template_id");

  const userRole = AuthHelpers.getUserType();

  const getAnnouncementTemplateTypeId = () => {
    if (postType === CommonEnums.postTypes.image_post) {
      return "announcement_for_sale_image_post";
    } else if (postType === CommonEnums.postTypes.story_post) {
      return "announcement_for_sale_story_post";
    }
    return "";
  };

  const actualTemplateTypeId = isAnnouncementPost
    ? getAnnouncementTemplateTypeId()
    : templateTypeId;

  const getImageClass = (type: string) => {
    switch (type) {
      case CommonEnums.postTypes.image_post:
        return "image-post-template";
      case CommonEnums.postTypes.video_post:
        return "video-post-template";
      case CommonEnums.postTypes.story_post:
        return "story-post-template";
      case CommonEnums.postTypes.reel_post:
        return "reel-post-template";
      default:
        return "template-image";
    }
  };

  const isVideoType = (type: string) =>
    [
      CommonEnums.postTypes.video_post,
      CommonEnums.postTypes.reel_post,
    ].includes(type);

  const getListApi = async () => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      let templatesData: any[] = [];

      const res = await TemplatesApi.getAgencyAnnouncementTemplates({
        announcement_type: announcementType,
      });

      if (res?.data?.success) {
        const imagePostData = res.data.data["image_post"] || {};

        const announcementTemplate = imagePostData.templates?.find(
          (template: any) =>
            template.slug === "announcement_for_sale_image_post"
        );

        if (announcementTemplate?.config?.length > 0) {
          let filteredConfigs = [...announcementTemplate.config];

          if (announcementType) {
            filteredConfigs = filteredConfigs.filter(
              (config: any) =>
                config.design_template?.announcement_type === announcementType
            );
          }

          templatesData = filteredConfigs.map((config: any) => {
            const dt = config.design_template;

            return {
              _id: config._id,
              name: dt?.name || "Announcement Template",
              design_template: dt,
              post_type: postType, // only for preview logic
              announcement_type: dt?.announcement_type,
              selected: dt?._id === urlTemplateId,
              imageClass: getImageClass(postType),
              isVideo: isVideoType(postType),
              design_template_id: dt?._id,
              is_announcement_config: true,
              status: "published",
            };
          });
        }
      } else {
        throw new Error(
          res?.data?.message || "Failed to load announcement templates"
        );
      }

      setState((prev) => ({
        ...prev,
        isLoading: false,
        listData: templatesData,
        totalPage: 1,
      }));
    } catch (error: any) {
      setState((prev) => ({ ...prev, isLoading: false }));
      toast.error(error.message || "Something went wrong");
    }
  };

  useEffect(() => {
    getListApi();
  }, [
    actualTemplateTypeId,
    postType,
    listingType,
    subscriptionType,
    announcementType,
    urlTemplateId,
  ]);

  const getPreviewImage = (designTemplate: any) => {
    if (!designTemplate) return "";

    const isAgency = userRole === CommonEnums.userRole.agency;

    if (postType === CommonEnums.postTypes.image_post) {
      return isAgency
        ? designTemplate?.agency_image_preview_file_ref?.compressed_file_path
        : designTemplate?.agent_image_preview_file_ref?.compressed_file_path;
    }

    if (postType === CommonEnums.postTypes.story_post) {
      return isAgency
        ? designTemplate?.agency_story_preview_file_ref?.compressed_file_path
        : designTemplate?.agent_story_preview_file_ref?.compressed_file_path;
    }

    return "";
  };

  const onTemplateSelect = (
    selectedTemplateId: string,
    imageUrl?: string,
    designTemplateId?: string
  ) => {
    const updatedList = state.listData.map((item) => ({
      ...item,
      selected: item.design_template_id === designTemplateId,
    }));

    setState((prev) => ({ ...prev, listData: updatedList }));

    if (onSelectTemplate) {
      const templateId = designTemplateId || selectedTemplateId;
      onSelectTemplate(templateId, imageUrl || "");
    }
  };

  return (
    <div className="select-templates-list-container">
      <h3 className="step-title">Select Announcement Template</h3>
      {state.isLoading ? (
        <div className="list-page-loading">
          <Loading />
        </div>
      ) : state.listData.length === 0 ? (
        <div className="data-not-found">
          <p>No announcement templates found!</p>
        </div>
      ) : (
        <div className="select-template-container">
          {state.listData.map((data: any) => (
            <div className="select-template-box" key={data._id}>
              <div className={`template-image-common ${data.imageClass}`}>
                <img
                  src={getImageUrl(getPreviewImage(data.design_template))}
                  alt={data?.name || "template"}
                />
                {data.isVideo && (
                  <div className="video-icon-bg">
                    <div className="video-icon">
                      <VidioIcon />
                    </div>
                  </div>
                )}
                {data.selected && (
                  <div className="selected-pill">
                    <RoundTrueIcon />
                    <span>Selected</span>
                  </div>
                )}
              </div>
              <div className="template-box-text-contain">
                <p className="template-box-text-title">{data.name}</p>
                <TemplateButtons
                  templateData={data}
                  onTemplateSelect={() =>
                    onTemplateSelect(
                      data._id,
                      getImageUrl(getPreviewImage(data.design_template)),
                      data.design_template_id
                    )
                  }
                  announcementPostId={announcementPostId}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SelectTemplatesList;
